import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Activity, ArrowRight, Info } from "lucide-react";
import api, { timeAgo } from "@/lib/api";
import { CARD_HERO, homeTrack } from "./shared";
import { AuroraAtmosphere, AuroraGauge, IndexSparkline, SignalTiles } from "./AuroraArc";

export const COLORS: Record<string, string> = {
  "Extreme Fear": "#EF4444", Fear: "#F87171", Neutral: "#A3A3A3", Greed: "#34D399", "Extreme Greed": "#10B981",
};

const reducedMotion = () =>
  typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const easeOut = (t: number) => 1 - Math.pow(1 - t, 5);
const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);

export const useGaugeSweep = (target: number | null, ready: boolean) => {
  const [display, setDisplay] = useState(0);
  const [done, setDone] = useState(false);
  const [glint, setGlint] = useState(false);
  const st = useRef({ raf: 0, started: false, last: null as number | null, disp: 0, up: true });

  useEffect(() => {
    if (target == null || !ready) return;
    const s = st.current;
    if (s.last === target) return;
    const initial = !s.started;
    s.started = true;
    s.last = target;
    cancelAnimationFrame(s.raf);
    if (reducedMotion()) {
      s.disp = target;
      setDisplay(target);
      setDone(true);
      return;
    }
    const from = initial ? 0 : s.disp;
    s.up = target >= from;
    const dur = initial ? 1100 : 700;
    const t0 = performance.now() + (initial ? 150 : 0);
    const ease = initial ? easeOut : easeOutCubic;
    const tick = (now: number) => {
      const t = Math.min(Math.max((now - t0) / dur, 0), 1);
      s.disp = from + (target - from) * ease(t);
      setDisplay(s.disp);
      if (t < 1) {
        s.raf = requestAnimationFrame(tick);
      } else {
        s.disp = target;
        setDisplay(target);
        setDone(true);
        if (initial) setGlint(true);
      }
    };
    s.raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(s.raf);
      if (s.disp !== target) {
        s.last = null;
        s.started = s.disp > 0;
      }
    };
  }, [target, ready]);

  return { display, done, glint, up: st.current.up };
};

/* ---------------- Holographic Ring (Concept B) ---------------- */

const START = 120;           // degrees, gap centered at bottom
const SWEEP = 300;
const SEG_N = 90;
const CX = 110;

const SPECTRUM: [number, string][] = [
  [0, "#C81E1E"], [0.18, "#F0564E"], [0.34, "#F97316"], [0.5, "#8B5CF6"],
  [0.64, "#34D399"], [0.8, "#06D6F5"], [1, "#3B82F6"],
];

const hex2rgb = (h: string) => [parseInt(h.slice(1, 3), 16), parseInt(h.slice(3, 5), 16), parseInt(h.slice(5, 7), 16)];

const colorAt = (t: number) => {
  const x = Math.max(0, Math.min(t, 1));
  let i = 0;
  while (i < SPECTRUM.length - 2 && x > SPECTRUM[i + 1][0]) i++;
  const [t0, c0] = SPECTRUM[i];
  const [t1, c1] = SPECTRUM[i + 1];
  const f = t1 === t0 ? 0 : (x - t0) / (t1 - t0);
  const a = hex2rgb(c0), b = hex2rgb(c1);
  return `rgb(${Math.round(a[0] + (b[0] - a[0]) * f)},${Math.round(a[1] + (b[1] - a[1]) * f)},${Math.round(a[2] + (b[2] - a[2]) * f)})`;
};

const pol = (deg: number, r: number): [number, number] => {
  const a = (deg * Math.PI) / 180;
  return [CX + r * Math.cos(a), CX + r * Math.sin(a)];
};

const ringArc = (v0: number, v1: number, r: number) => {
  const [x0, y0] = pol(START + v0 * 3, r);
  const [x1, y1] = pol(START + v1 * 3, r);
  return `M ${x0} ${y0} A ${r} ${r} 0 ${(v1 - v0) * 3 > 180 ? 1 : 0} 1 ${x1} ${y1}`;
};

const SEGS = Array.from({ length: SEG_N }, (_, i) => {
  const v = (i / (SEG_N - 1)) * 100;
  const ang = START + v * 3;
  const rad = (ang * Math.PI) / 180;
  const px = Math.cos(rad + Math.PI / 2) * 0.75;
  const py = Math.sin(rad + Math.PI / 2) * 0.75;
  const [x1, y1] = pol(ang, 67);
  const [x2, y2] = pol(ang, 85);
  const [h1x, h1y] = pol(ang, 70);
  const [h2x, h2y] = pol(ang, 82.5);
  return { v, x1, y1, x2, y2, color: colorAt(v / 100),
    hx1: h1x + px, hy1: h1y + py, hx2: h2x + px, hy2: h2y + py };
});

const SPARKLES = [
  { v: 79, s: 1, d: "0s" },
  { v: 33, s: 0.6, d: "1.7s" },
  { v: 96, s: 0.45, d: "3.2s" },
];

const DOTS = Array.from({ length: 56 }, (_, i) => {
  const [x, y] = pol(START + (i / 55) * 300, 105);
  return { x, y, o: 0.08 + ((i * 29) % 7) * 0.028 };
});

export const HoloRing = ({ pct, value, animating, glint, num, label, labelColor, delta, done, dormant = false, size = "sm", sparkles = true }: {
  pct: number; value: number | null; animating: boolean; glint: boolean;
  num: string | number; label?: string; labelColor?: string; delta?: number | null;
  done: boolean; dormant?: boolean; size?: "xs" | "sm" | "lg"; sparkles?: boolean;
}) => {
  const p = Math.max(0, Math.min(pct, 100));
  const [bx, by] = pol(START + p * 3, 76);
  const lg = size === "lg";
  const xs = size === "xs";
  const dcolor = delta != null && delta > 0 ? "text-[#34D399]" : delta != null && delta < 0 ? "text-[#EF4444]" : "text-gray-500";
  return (
    <div className="relative w-full" aria-hidden>
      <svg viewBox="0 0 220 220" className="w-full">
        <defs>
          <filter id="holo-blur" x="-30%" y="-30%" width="160%" height="160%">
            <feGaussianBlur stdDeviation="3" />
          </filter>
          <filter id="holo-blur2" x="-60%" y="-60%" width="220%" height="220%">
            <feGaussianBlur stdDeviation="1.6" />
          </filter>
          <radialGradient id="holo-well" cx="50%" cy="46%" r="60%">
            <stop offset="0%" stopColor="#08090c" />
            <stop offset="72%" stopColor="#0a0b10" />
            <stop offset="100%" stopColor="#151228" />
          </radialGradient>
        </defs>

        {/* outer holographic halo */}
        <g className="transition-opacity duration-300 group-hover:opacity-[1.08]" opacity="1">
          <path d={ringArc(0, 100, 97)} fill="none" stroke="#7C3AED" strokeWidth="4"
            opacity={dormant ? 0.08 : 0.17} filter="url(#holo-blur)" strokeLinecap="round" />
          <path d={ringArc(62, 100, 97)} fill="none" stroke="#00E5FF" strokeWidth="3"
            opacity={dormant ? 0.06 : 0.14} filter="url(#holo-blur)" strokeLinecap="round" />
          <path d={ringArc(0, 100, 97)} fill="none" stroke="#8B5CF6" strokeWidth="1.1"
            opacity={dormant ? 0.22 : 0.5} strokeLinecap="round" />
          <path d={ringArc(70, 100, 97)} fill="none" stroke="#00E5FF" strokeWidth="1"
            opacity={dormant ? 0.15 : 0.42} strokeLinecap="round" />
          <path d={ringArc(0, 100, 101.5)} fill="none" stroke="#7C3AED" strokeWidth="0.6"
            opacity={dormant ? 0.07 : 0.14} strokeLinecap="round" />
        </g>

        {/* halo sparkles — light catching the outer ring */}
        {sparkles && !dormant && SPARKLES.map((sp, i) => {
          const [x, y] = pol(START + sp.v * 3, 97);
          return (
            <g key={i} className="holo-sparkle" style={{ animationDelay: sp.d }}
              transform={`translate(${x} ${y}) scale(${sp.s})`}>
              <circle r="3.4" fill="#fff" opacity="0.16" filter="url(#holo-blur2)" />
              <path d="M 0 -4.4 L 0.75 -0.75 L 4.4 0 L 0.75 0.75 L 0 4.4 L -0.75 0.75 L -4.4 0 L -0.75 -0.75 Z"
                fill="#fff" opacity="0.85" />
              <path d="M 0 -2.4 L 0.5 -0.5 L 2.4 0 L 0.5 0.5 L 0 2.4 L -0.5 0.5 L -2.4 0 L -0.5 -0.5 Z"
                fill="#00E5FF" opacity="0.5" />
              <circle r="0.7" fill="#fff" />
            </g>
          );
        })}

        {/* micro-dot perimeter */}
        {DOTS.map((d, i) => (
          <circle key={i} cx={d.x} cy={d.y} r="0.65" fill="#A78BFA" opacity={dormant ? d.o * 0.5 : d.o} />
        ))}

        {/* recessed track */}
        <path d={ringArc(0, 100, 76)} fill="none" stroke="#000" strokeOpacity="0.55" strokeWidth="23" strokeLinecap="round" />
        <path d={ringArc(0, 100, 76)} fill="none" stroke="#15161d" strokeWidth="20" strokeLinecap="round" />

        {/* active under-glow */}
        {!dormant && (
          <g filter="url(#holo-blur)" opacity="0.5">
            {SEGS.filter((s) => s.v <= p).map((s, i) => (
              <line key={i} x1={s.x1} y1={s.y1} x2={s.x2} y2={s.y2} stroke={s.color}
                strokeWidth="2.8" strokeLinecap="round" />
            ))}
          </g>
        )}

        {/* segmented spectrum: full bed + active range (glass sticks) */}
        {SEGS.map((s, i) => (
          <line key={i} x1={s.x1} y1={s.y1} x2={s.x2} y2={s.y2} stroke={s.color}
            strokeWidth="2.4" strokeLinecap="round"
            opacity={dormant ? 0.14 : s.v <= p ? 0.94 : 0.26} />
        ))}
        {/* glass specular edge on each stick */}
        {SEGS.map((s, i) => (
          <line key={i} x1={s.hx1} y1={s.hy1} x2={s.hx2} y2={s.hy2} stroke="#fff"
            strokeWidth="0.7" strokeLinecap="round"
            opacity={dormant ? 0.04 : s.v <= p ? 0.38 : 0.1} />
        ))}
        {/* broad glass sheen where light falls across the band */}
        <path d={ringArc(28, 72, 76)} fill="none" stroke="#fff" strokeWidth="18"
          strokeLinecap="round" opacity="0.045" />

        {/* segment depth: dark inner edge + light outer edge */}
        <path d={ringArc(0, 100, 68)} fill="none" stroke="#000" strokeOpacity="0.32" strokeWidth="3.5" />
        <path d={ringArc(0, 100, 84.5)} fill="none" stroke="#fff" strokeOpacity="0.05" strokeWidth="1.6" />

        {/* inner depth well */}
        <circle cx={CX} cy={CX} r="63" fill="url(#holo-well)" />
        <circle cx={CX} cy={CX} r="63.5" fill="none" stroke="#fff" strokeOpacity="0.05" strokeWidth="0.6" />
        {!dormant && value != null && (
          <circle cx={CX} cy={CX} r="58" fill={colorAt(value / 100)} opacity="0.06" />
        )}

        {/* traveling sheen at leading edge during reveal */}
        {animating && p > 1 && (
          <g>
            <path d={ringArc(Math.max(0, p - 7), p, 76)} fill="none" stroke="#fff"
              strokeWidth="20" opacity="0.28" filter="url(#holo-blur2)" strokeLinecap="round" />
          </g>
        )}

        {/* active endpoint bead */}
        {!dormant && p > 0.5 && (
          <g>
            <circle cx={bx} cy={by} r="4.5" fill="#00E5FF" opacity="0.3" filter="url(#holo-blur2)" />
            <circle cx={bx} cy={by} r="1.7" fill="#fff" opacity="0.95" />
          </g>
        )}

        {/* final glint */}
        {glint && (
          <g className="lic-glint">
            <circle cx={bx} cy={by} r="6" fill="#00E5FF" opacity="0.4" filter="url(#holo-blur)" />
            <circle cx={bx} cy={by} r="2" fill="#fff" />
          </g>
        )}

        {/* scale endpoints */}
        <text x={pol(114, 96)[0]} y={pol(114, 96)[1] + 3} textAnchor="middle" fontSize="9"
          fontFamily="JetBrains Mono, monospace" fill="#6b7280">0</text>
        <text x={pol(66, 96)[0]} y={pol(66, 96)[1] + 3} textAnchor="middle" fontSize="9"
          fontFamily="JetBrains Mono, monospace" fill="#6b7280">100</text>
      </svg>

      {/* center typography */}
      <div className={`absolute inset-0 flex flex-col items-center justify-center text-center ${xs ? "px-6" : "px-8"}`}>
        <span data-testid="layer-index-value"
          className={`font-mono font-bold leading-none tracking-tight text-[#f2f5fc] ${lg ? "text-[52px]" : xs ? "text-[26px]" : "text-[38px]"}`}
          style={{ textShadow: "0 0 24px rgba(0,229,255,0.15), 0 1px 10px rgba(255,255,255,0.12)" }}>
          {num}
        </span>
        {label && (
          <p data-testid="layer-index-label" key={label}
            className={`${done ? "lic-fade" : "opacity-0"} font-mono font-bold uppercase ${
              lg ? "text-[13px] tracking-[0.24em] mt-1.5" : xs ? "text-[8px] tracking-[0.14em] mt-1" : "text-[11px] tracking-[0.2em] mt-1.5"}`}
            style={{ color: labelColor }}>
            {label}
          </p>
        )}
        {delta != null && (
          <p data-testid="layer-index-delta"
            className={`transition-all duration-500 ${done ? "opacity-100 translate-y-0" : "opacity-0 translate-y-1"} font-mono ${
              lg ? "text-[12px] mt-1" : xs ? "text-[8px] mt-0.5" : "text-[10.5px] mt-1"} flex flex-col items-center`}
            style={{ transitionDelay: done ? "120ms" : "0ms" }}>
            <span className="flex items-center justify-center gap-1">
              <span className={dcolor}>{delta > 0 ? "↑" : delta < 0 ? "↓" : "→"}</span>
              <span className={`font-bold ${dcolor}`}>{Math.abs(delta)} pts</span>
            </span>
            <span className="text-gray-600 mt-0.5">in 24h</span>
          </p>
        )}
      </div>
    </div>
  );
};

/* ---------------- Homepage card — Aurora Arc (Founder approved) ---------------- */

const updLabel = (iso: string) =>
  timeAgo(iso).replace(/ minutes?/, "m").replace(/ hours?/, "h").replace(/ days?/, "d")
    .replace("just now", "now").replace(" ago", "");

export const LayerIndexCard = () => {
  const cardRef = useRef<HTMLDivElement>(null);
  const [seen, setSeen] = useState(false);
  const { data, isError } = useQuery({
    queryKey: ["layer-index"],
    queryFn: async () => (await api.get("/layer-index")).data,
    staleTime: 5 * 60 * 1000,
  });
  const history: { date: string; value: number }[] = data?.history || [];

  const value: number | null = data?.value ?? null;
  const { display, done, glint, up } = useGaugeSweep(value, seen);

  useEffect(() => {
    const el = cardRef.current;
    if (!el || seen) return;
    if (!("IntersectionObserver" in window)) { setSeen(true); return; }
    const io = new IntersectionObserver((es) => {
      if (es.some((e) => e.isIntersecting)) { setSeen(true); io.disconnect(); }
    }, { threshold: 0.3 });
    io.observe(el);
    return () => io.disconnect();
  }, [seen, data]);

  const loading = !data && !isError;
  const color = data ? (COLORS[data.label] || "#A3A3A3") : "#A3A3A3";
  const yesterday = history.length > 1 ? history[history.length - 2].value : null;
  const delta = value != null && yesterday != null ? value - yesterday : null;
  const c = data?.components || {};

  const pct = Math.max(0, Math.min(display, 100));
  const num = value == null ? "" : done ? value : Math.round(up ? Math.min(display, value) : Math.max(display, value));

  return (
    <div ref={cardRef} data-testid="layer-index-card"
      className={`${CARD_HERO} group relative overflow-hidden p-4 flex flex-col h-full transition-colors duration-300 hover:border-[#7C3AED]/40`}>
      <AuroraAtmosphere />

      {data && value != null && (
        <p className="sr-only">
          Layer Index {value} out of 100 — {data.label}.
          {delta != null ? ` ${delta > 0 ? "Up" : delta < 0 ? "Down" : "Unchanged,"} ${Math.abs(delta)} points in 24 hours.` : ""}
          {c.momentum != null ? ` Momentum ${Math.round(c.momentum)}, sentiment ${Math.round(c.sentiment)}.` : ""}
        </p>
      )}

      <div className="relative flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-[7px] border border-[#7C3AED]/40 bg-[#7C3AED]/15">
            <Activity aria-hidden className="h-3 w-3 text-[#c4a6ff]" />
          </span>
          <span className="nl-sg text-[13px] font-semibold tracking-[0.08em] text-[#f2f5fc] whitespace-nowrap">
            LAYER INDEX
          </span>
        </div>
        <span className="relative group/info mt-0.5">
          <button type="button" aria-label="What is the Layer Index?" data-testid="layer-index-info"
            className="block text-gray-600 hover:text-gray-300 focus:outline-none focus-visible:text-gray-300 transition-colors">
            <Info className="h-3.5 w-3.5" />
          </button>
          <span role="tooltip"
            className="pointer-events-none absolute right-0 top-full mt-2 w-56 z-30 rounded-lg border border-white/10 bg-[#0d0e14]/[0.98] p-2.5 font-sans font-normal normal-case tracking-normal text-[10.5px] leading-snug text-gray-400 opacity-0 translate-y-1 transition-all duration-200 group-hover/info:opacity-100 group-hover/info:translate-y-0 group-focus-within/info:opacity-100 group-focus-within/info:translate-y-0 shadow-xl">
            Live market mood, 0 (extreme fear) to 100 (extreme greed) — scored from NewsLayer coverage:
            headline sentiment, BTC &amp; ETH momentum and breaking-news activity over the last 24h.
          </span>
        </span>
      </div>
      <p className="relative nl-geist text-[11.5px] leading-snug text-[#98a0b5] mt-1.5">
        NewsLayer's real-time signal score for the onchain economy.
      </p>

      <div className="relative mx-auto w-full max-w-[216px] mt-1.5">
        <AuroraGauge pct={isError ? 0 : pct} num={isError ? "—" : num} label={data?.label} labelColor={color}
          delta={delta} done={done} animating={seen && !!data && !done} glint={glint} dormant={loading || isError} />
      </div>
      {isError && (
        <p className="relative text-center nl-mono text-[10px] text-gray-500 mt-1" data-testid="layer-index-error">
          Layer Index temporarily unavailable
        </p>
      )}

      {history.length > 1 && (
        <div className="relative mt-2">
          <IndexSparkline history={history} />
        </div>
      )}

      {data && (
        <div className="relative mt-2.5 mb-2.5">
          <SignalTiles components={c} />
        </div>
      )}

      <div className="relative mt-auto pt-2.5 border-t border-white/[0.05] flex items-center justify-between gap-2">
        <span data-testid="layer-index-updated" className="nl-mono text-[10px] uppercase tracking-wider text-[#8a92a8] whitespace-nowrap">
          {data?.computed_at ? `Updated ${updLabel(data.computed_at)}` : ""}
        </span>
        <Link to="/layer-index" data-testid="layer-index-why"
          onClick={() => homeTrack("layer_index", "how_it_works")}
          className="group/cta flex items-center gap-1 nl-geist text-[12px] font-semibold text-[#0dd6eb] hover:text-white transition-colors whitespace-nowrap">
          How it works
          <ArrowRight className="h-3 w-3 transition-transform duration-200 group-hover/cta:translate-x-0.5" />
        </Link>
      </div>
    </div>
  );
};

